/**
 * bb-region-filter.js
 * ───────────────────────────────────────────────────────────────────────────
 * Client-side region filtering (spec §3). Shows/hides product cards by
 * membership of the visitor's resolved region in each card's region tags.
 *
 * Rules (launch scope = served regions A–C):
 *   - SERVED visitor (region ∈ A–C):
 *       · show IF region ∈ card.regions (membership → multi-region products work)
 *       · hide IF region ∉ card.regions; UNTAGGED card → hidden (fail-closed)
 *   - UNKNOWN visitor (region null — geo failed/denied/pending, theme editor) OR
 *     KNOWN-UNSERVED visitor (D–I / AK-HI / non-US) → show ALL, ignoring tags, so
 *     the store is never empty. The PDP (bb-availability.js) decides buy vs. the
 *     notify panel; the grid just lets everyone browse the full catalog.
 *   - REGION-EXEMPT card (data-region-exempt, set by bb-region-exempt.liquid) →
 *     always shown, whoever the visitor is.
 *
 * This is a UX layer, not enforcement: cards stay in the DOM (crawler-visible).
 * True purchase prevention would need a server-side checkout guardrail — out of
 * scope here (spec §0.3 / §10).
 *
 * Re-render aware: Horizon repaints the grid via infinite scroll, facet
 * section-morphs, and predictive search. A MutationObserver re-applies the
 * filter to cards added or repurposed after the initial pass, and a
 * `filter:update` listener re-sweeps after facet changes.
 */

import { getRegionTags, isServedRegion, isRegionExempt } from '@theme/bb-region-config';
import { getLocation } from '@theme/bb-location-store';
import { BB_LOCATION_EVENT, getLastResolved } from '@theme/bb-geo-detect';

const CARD_SELECTOR = 'product-card';
/**
 * The element to collapse when a card is filtered out.
 *
 * Two wrappers, because the theme has two: collection grids wrap each card in
 * `.product-grid__item`, while `product-list`, `product-recommendations` and
 * `collection-list` use `.resource-list__item`. Only the first was listed, so in
 * every one of those sections hostFor() fell through to the <product-card>
 * itself — the card went invisible but its cell kept its track, leaving blank
 * holes in the grid and empty slides in the carousels.
 *
 * `.resource-list__item` is also the carousel SLIDE (there is no separate slide
 * element for product cards — `slideshow-slide` belongs to the product media
 * gallery), so collapsing it is what removes the empty slide too.
 */
const ITEM_SELECTOR = '.product-grid__item, .resource-list__item';
const HIDDEN_CLASS = 'bb-region-hidden';
const FILTER_ATTR = 'data-bb-filtering';
/** Horizon dispatches this on `document` when collection facets change. */
const FILTER_UPDATE_EVENT = 'filter:update';

let _region = null;
let _resolved = false;
let _observer = null;
let _sweepQueued = false;
/** Guards the fail-open console.error so a repainting grid can't spam it. */
let _warnedMissingTags = false;
/** True once a resolution has been applied to the DOM and the grid revealed —
 *  see handleResolution's post-paint latch below. */
let _paintedOnce = false;

/** The element to hide: the grid item (so its cell collapses) or the card. */
function hostFor(card) {
  return card.closest(ITEM_SELECTOR) || card;
}

/**
 * Parsed region tags for a card, memoized on the element and invalidated when
 * its `data-regions` value changes (e.g. a morph repurposes the card). Avoids
 * re-parsing every card's tags on each full sweep.
 */
function cardTags(card) {
  const raw = card.getAttribute('data-regions') || '';
  if (card.__bbTagsRaw !== raw || !card.__bbTags) {
    card.__bbTagsRaw = raw;
    card.__bbTags = getRegionTags(raw);
  }
  return card.__bbTags;
}

/**
 * True once bb-flash-guard.liquid's safety timeout has already revealed the grid
 * unfiltered, i.e. detection did not resolve within 1.5s.
 *
 * From that moment the page is COMMITTED to show-all. Filtering afterwards would
 * mean removing products from a grid the shopper has been reading for seconds —
 * on a 61-product collection, most of it disappearing at once, potentially under
 * a cursor already travelling toward a card. A grid that is too permissive is
 * recoverable (the PDP gate still refuses the sale); a grid that rearranges
 * itself after paint is not.
 *
 * Deliberately read live rather than latched at boot: the timeout can fire after
 * this module has initialised but before detection resolves, which is exactly
 * the race being closed.
 */
function committedToShowAll() {
  return typeof document !== 'undefined' && document.documentElement.getAttribute(FILTER_ATTR) === 'timeout';
}

/**
 * Membership rule:
 *   - Grid already revealed unfiltered by the safety timeout → show everything,
 *     permanently for this page view (see committedToShowAll).
 *   - REGION-EXEMPT card (design services) → always shown, tags ignored. These
 *     are sold everywhere, so they must never be filtered out of a grid — not
 *     even for a served visitor whose region is missing from their tags.
 *   - SERVED visitor (region ∈ A–C) → show only cards tagged for the region
 *     (untagged fails closed)
 *   - UNKNOWN or KNOWN-UNSERVED visitor → show everything (browse all)
 */
function shouldShow(card) {
  if (committedToShowAll()) return true;
  if (isRegionExempt(card)) return true;
  if (!isServedRegion(_region)) return true;
  return cardTags(card).includes(_region);
}

function applyToCard(card) {
  hostFor(card).classList.toggle(HIDDEN_CLASS, !shouldShow(card));
}

/**
 * Fail-open guard.
 *
 * `data-regions` reaches cards through bb-region-tags.liquid, which is injected
 * into FIVE stock Horizon files (product-card, quick-add, buy-buttons,
 * product-information, and the card's own template). A theme update that rewrites
 * any of them silently drops the attribute — and because shouldShow() treats an
 * untagged card as not-in-region, a served visitor would then see an EMPTY
 * catalog with no error anywhere.
 *
 * If cards are present but not one carries the attribute, the injection is broken
 * rather than the catalog being genuinely unavailable. Show everything: an
 * over-permissive grid is recoverable, an empty store is lost revenue.
 */
function injectionIsIntact(cards) {
  for (const card of cards) {
    if (card.hasAttribute('data-regions')) return true;
  }
  return false;
}

function applyAll(root) {
  const scope = root && root.querySelectorAll ? root : document;
  const cards = [...scope.querySelectorAll(CARD_SELECTOR)];
  if (!cards.length) return;

  if (isServedRegion(_region) && !injectionIsIntact(cards)) {
    if (!_warnedMissingTags) {
      _warnedMissingTags = true;
      console.error(
        `[bb-region-filter] ${cards.length} product card(s) found but none carries ` +
          'data-regions. The bb-region-tags.liquid injection is missing or broken — ' +
          'failing OPEN and showing all products. Region filtering is NOT active.'
      );
    }
    for (const card of cards) hostFor(card).classList.remove(HIDDEN_CLASS);
    return;
  }

  for (const card of cards) applyToCard(card);
}

/** Reveal the grid once filtering has been applied (clears flash-mitigation).
 *  Never downgrades a 'timeout' back to 'ready': that attribute value is the
 *  record that the grid was already shown unfiltered, and committedToShowAll()
 *  reads it on every later sweep — including MutationObserver sweeps from
 *  infinite scroll and facet morphs, which would otherwise start filtering
 *  newly-arrived cards while the ones already on screen stayed. */
function reveal() {
  if (typeof document === 'undefined') return;
  if (document.documentElement.getAttribute(FILTER_ATTR) === 'timeout') return;
  document.documentElement.setAttribute(FILTER_ATTR, 'ready');
}

/**
 * Apply a resolution to the grid — but only ONCE per page view.
 *
 * boot() applies a cached resolution synchronously so a returning visitor's
 * grid paints filtered from the start; a first-time visitor gets their only
 * resolution from the later bb:location-resolved event. Either way, that is
 * the paint the shopper sees and starts reading/reaching into. A SECOND
 * resolution (ipapi correcting a cached guess, or the visitor confirming a
 * new ZIP) used to re-run applyAll() over that already-painted grid — cards
 * appearing or vanishing under a cursor already moving toward one.
 *
 * So the first resolution paints normally and latches; every resolution after
 * it updates `_region` (so anything that arrives LATER — infinite scroll,
 * facet morphs, via queueSweep/the MutationObserver below — is still filtered
 * correctly) but does not touch what's already on screen.
 *
 * EXCEPT a CONFIRMED region change. The latch exists to protect an
 * already-painted grid from a later auto-detection *refinement* (ipapi
 * correcting its own guess) — not from the shopper explicitly telling us their
 * region is different, e.g. entering a new ZIP in the header pill
 * (bb-zip-lookup.js), which re-emits BB_LOCATION_EVENT with no page reload. If
 * the latch swallowed that too, the grid would keep showing the OLD region's
 * products (and hiding ones the shopper can now buy) while the PDP gate
 * (bb-availability.js, which has no latch) had already moved on — visibly
 * disagreeing on the same visit. So: re-run the paint when this resolution is
 * `zip_confidence: 'confirmed'` AND it actually changed the resolved region;
 * a confirmed re-submit of the same region, or any unconfirmed
 * (auto-guess) resolution, still respects the latch.
 */
function handleResolution(detail) {
  const previousRegion = _region;
  _region = detail && detail.region != null ? detail.region : null;
  _resolved = true;
  const isConfirmedChange = Boolean(detail) && detail.zip_confidence === 'confirmed' && _region !== previousRegion;
  if (_paintedOnce && !isConfirmedChange) return;
  _paintedOnce = true;
  applyAll(document);
  reveal();
}

/** Coalesce bursts of mutations / facet updates into one sweep per frame. */
function queueSweep() {
  if (_sweepQueued || !_resolved) return;
  _sweepQueued = true;
  requestAnimationFrame(() => {
    _sweepQueued = false;
    applyAll(document);
  });
}

function startObserver() {
  if (_observer || typeof MutationObserver === 'undefined' || !document.body) return;

  _observer = new MutationObserver((mutations) => {
    if (!_resolved) return; // pending CSS hides new cards; applyAll on resolve covers them

    const cards = new Set();
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(CARD_SELECTOR)) cards.add(node);
          for (const inner of node.querySelectorAll(CARD_SELECTOR)) cards.add(inner);
        }
      } else if (mutation.type === 'attributes') {
        // A morph can repurpose an existing card for a different product.
        const target = mutation.target;
        if (target instanceof Element && target.matches(CARD_SELECTOR)) cards.add(target);
      }
    }
    for (const card of cards) applyToCard(card);
  });

  _observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-id', 'data-regions', 'data-region-exempt'],
  });
}

function boot() {
  if (typeof document === 'undefined') return;

  // Subscribe BEFORE reading the last value so a resolution firing in between
  // is never missed (re-applying is idempotent if both paths run).
  document.addEventListener(BB_LOCATION_EVENT, (event) => handleResolution(event.detail));
  document.addEventListener(FILTER_UPDATE_EVENT, queueSweep);
  startObserver();

  // PERF/LCP: reveal as early as possible. A returning visitor has a cached
  // location in localStorage, so apply + reveal synchronously here — this takes
  // every repeat visit off the ipapi round-trip entirely (no waiting on the
  // network to un-hide the grid). First visitors fall through to the detection
  // event (and the flash-guard safety timeout caps the worst case).
  //
  // Skip the cached path in the theme editor: detection emits an unmapped
  // (show-all) result in design mode, and applying a stale live-store cache here
  // would briefly geofence the editor preview before that event corrects it.
  const inDesignMode = typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
  if (!inDesignMode) {
    const cached = getLocation();
    if (cached) {
      handleResolution(cached);
      return;
    }
  }

  const last = getLastResolved();
  if (last !== undefined) handleResolution(last);
}

boot();
